import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signOut, supabase, type AuthSession } from "../auth";
import { useCurrentGarden } from "../lib/useCurrentGarden";
import {
  joinGardenByCode,
  leaveGarden,
  regenerateJoinCode,
} from "../db/gardenMembership";
import "./More.css";

interface MoreProps {
  session: AuthSession;
}

export function More({ session }: MoreProps) {
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const email = session.user.email ?? "anonymous";

  const onLogout = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/login", { replace: true });
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="More">
      <header className="More__head">
        <div className="More__eyebrow">Account</div>
        <div className="More__email">{email}</div>
      </header>

      <SharePanel />

      <ul className="More__list">
        <li className="More__item">
          <Link to="/tasks" className="More__link">Tasks</Link>
        </li>
        <li className="More__item">
          <Link to="/observations" className="More__link">Observations</Link>
        </li>
        <li className="More__item More__item--disabled">Health</li>
      </ul>

      <button
        type="button"
        className="More__signout"
        onClick={onLogout}
        disabled={signingOut}
      >
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
    </div>
  );
}

/** Garden share / join / leave UI. The whole multi-user surface in one
 *  panel. See ADR-0026. */
function SharePanel() {
  const { current, gardens, refresh, setCurrentGardenId } = useCurrentGarden();
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState<"none" | "join" | "leave" | "regen" | "copy">(
    "none",
  );
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!current) return null;

  const onCopy = async () => {
    setBusy("copy");
    try {
      await navigator.clipboard.writeText(current.join_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy join code:", current.join_code);
    } finally {
      setBusy("none");
    }
  };

  const onRegen = async () => {
    if (!supabase) return;
    if (!confirm("Generate a new code? The old one stops working immediately.")) return;
    setBusy("regen");
    setError(null);
    try {
      await regenerateJoinCode(supabase, current.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("none");
    }
  };

  const onLeave = async () => {
    if (!supabase) return;
    if (!confirm(`Leave "${current.name}"? You'll stop seeing its plants and tasks.`)) return;
    setBusy("leave");
    setError(null);
    try {
      await leaveGarden(supabase, current.id);
      const remaining = gardens.find((g) => g.id !== current.id);
      if (remaining) setCurrentGardenId(remaining.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("none");
    }
  };

  const onJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    if (!joinCode.trim()) return;
    setBusy("join");
    setError(null);
    try {
      const garden = await joinGardenByCode(supabase, joinCode);
      await refresh();
      setCurrentGardenId(garden.id);
      setJoinCode("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("none");
    }
  };

  return (
    <section className="MoreShare">
      <div className="MoreShare__eyebrow">Active garden</div>
      <div className="MoreShare__name">{current.name}</div>

      <div className="MoreShare__codeRow">
        <span className="MoreShare__codeLabel">Join code</span>
        <code className="MoreShare__code">{current.join_code}</code>
        <button
          type="button"
          className="MoreShare__btn MoreShare__btn--ghost"
          onClick={onCopy}
          disabled={busy === "copy"}
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          type="button"
          className="MoreShare__btn MoreShare__btn--ghost"
          onClick={onRegen}
          disabled={busy === "regen"}
        >
          {busy === "regen" ? "…" : "New code"}
        </button>
      </div>

      <div className="MoreShare__hint">
        Share the code with a partner to give them access. Anyone with the
        code can join. Regenerating invalidates it instantly.
      </div>

      <div className="MoreShare__actions">
        <button
          type="button"
          className="MoreShare__btn MoreShare__btn--danger"
          onClick={onLeave}
          disabled={busy === "leave"}
        >
          {busy === "leave" ? "Leaving…" : "Leave this garden"}
        </button>
      </div>

      <form className="MoreShare__joinRow" onSubmit={onJoin}>
        <label className="MoreShare__joinLabel" htmlFor="MoreShare__joinInput">
          Join another garden
        </label>
        <div className="MoreShare__joinFields">
          <input
            id="MoreShare__joinInput"
            type="text"
            className="MoreShare__joinInput"
            placeholder="Paste a code"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="submit"
            className="MoreShare__btn MoreShare__btn--primary"
            disabled={busy === "join" || !joinCode.trim()}
          >
            {busy === "join" ? "Joining…" : "Join"}
          </button>
        </div>
      </form>

      {error && <p className="MoreShare__error">{error}</p>}
    </section>
  );
}
